// ============================================================================
//  Reparto de balizas en los cuatro carriles del mapa de portal de /pruebas
//  (Fuera / Entrando ↓ / Saliendo ↑ / Dentro).
//
//  Vive aparte de client/pages/pruebas.ts, y sin tocar el DOM, por una razón
//  concreta: acá estuvo escondido un bug durante meses y la única forma de que
//  no vuelva es poder fijarlo en un test (pruebas.ts arranca el socket al
//  importarse, así que no se puede importar desde un spec).
//
//  LA REGLA QUE SE EQUIVOCABA: quien va SALIENDO sigue figurando DENTRO hasta
//  que el escáner exterior confirme la salida — así funciona el motor de
//  presencia, y así debe ser ("el silencio nunca es salida"). Con la prioridad
//  ingenua "inside gana siempre" el carril Saliendo ↑ quedaba INALCANZABLE:
//  toda salida normal se pintaba en Dentro, y en la pantalla no se veía salir
//  a nadie nunca. Para una baliza con dirección 'out' el carril más avanzado
//  ES Saliendo; estar dentro es justamente el estado que está dejando.
//
//  Cada baliza cae en UN solo carril. Prioridad, de más a menos avanzado:
//    Saliendo ('out', esté dentro o no) > Dentro > Entrando > Fuera.
// ============================================================================
import type {
  DetectedBeacon,
  InsideBeacon,
  StateSnapshot,
  TransitBeacon,
} from '../../src/shared/types';

export interface PortalLanes {
  /** dentro y quietos — los que están saliendo NO están acá */
  dentro: Map<string, InsideBeacon[]>;
  /** entrando y saliendo, con su `direction` para separarlos en dos carriles */
  enTransito: Map<string, TransitBeacon[]>;
  /** vistos por algún escáner, sin ingreso ni tránsito */
  fuera: Map<string, DetectedBeacon[]>;
}

function groupBy<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    const list = out.get(k);
    if (list) list.push(row);
    else out.set(k, [row]);
  }
  return out;
}

export function portalLanes(snap: StateSnapshot): PortalLanes {
  const insideMacs = new Set(snap.inside.map((b) => b.beaconMac));
  const transitMacs = new Set(snap.transit.map((t) => t.beaconMac));
  const saliendoMacs = new Set(
    snap.transit.filter((t) => t.direction === 'out').map((t) => t.beaconMac),
  );

  // el área de cada carril sale del propio registro: inside/transit la traen
  // del motor de presencia, detected del último escáner que vio la baliza.
  return {
    dentro: groupBy(
      snap.inside.filter((b) => !saliendoMacs.has(b.beaconMac)),
      (b) => b.area,
    ),
    enTransito: groupBy(
      snap.transit.filter((t) => !insideMacs.has(t.beaconMac) || t.direction === 'out'),
      (t) => t.area,
    ),
    fuera: groupBy(
      snap.detected.filter((d) => !insideMacs.has(d.beaconMac) && !transitMacs.has(d.beaconMac)),
      (d) => d.area,
    ),
  };
}
